import type { MediaAsset } from "./types";

export type MediaLibraryStatus = "loading" | "ready" | "uploading" | "error";

type FileLike = Pick<File, "name" | "type" | "size" | "arrayBuffer">;

export function canUpload(status: MediaLibraryStatus): boolean {
  return status === "ready";
}

export function normalizeMediaName(name: string): string {
  return name.trim().normalize("NFKC").toLocaleLowerCase();
}

export function hasSameMetadata(file: Pick<FileLike, "name" | "type" | "size">, asset: MediaAsset): boolean {
  return normalizeMediaName(file.name) === normalizeMediaName(asset.originalName)
    && file.type.toLocaleLowerCase() === asset.mimeType.toLocaleLowerCase()
    && file.size === asset.byteSize;
}

export function hasSameNameAndMime(file: Pick<FileLike, "name" | "type">, asset: MediaAsset): boolean {
  return normalizeMediaName(file.name) === normalizeMediaName(asset.originalName)
    && file.type.toLocaleLowerCase() === asset.mimeType.toLocaleLowerCase();
}

export async function contentSha256(file: FileLike): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  const digest = await globalThis.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function findDuplicate(file: FileLike, assets: MediaAsset[]): Promise<MediaAsset | null> {
  const candidates = assets.filter((asset) => hasSameMetadata(file, asset) && /^[a-f0-9]{64}$/i.test(asset.sha256));
  if (!candidates.length) return null;
  const hash = await contentSha256(file);
  return hash ? candidates.find((asset) => asset.sha256 === hash) ?? null : null;
}

export function mergeMediaAssets(current: MediaAsset[], incoming: MediaAsset[]): MediaAsset[] {
  const incomingIds = new Set(incoming.map((asset) => asset.id));
  return [...incoming, ...current.filter((asset) => !incomingIds.has(asset.id))];
}

export function duplicateMediaFromError(reason: unknown): MediaAsset | null {
  if (!reason || typeof reason !== "object") return null;
  const error = reason as { code?: unknown; details?: { media?: unknown } };
  const media = error.details?.media;
  if (error.code !== "MEDIA_ALREADY_EXISTS" || !media || typeof media !== "object") return null;
  const asset = media as Partial<MediaAsset>;
  return typeof asset.id === "string" && typeof asset.originalName === "string" && typeof asset.mimeType === "string"
    && typeof asset.byteSize === "number" && typeof asset.sha256 === "string" ? asset as MediaAsset : null;
}
